/*
 * Relatorio de atendimento: quantos chamados, em quanto tempo e quantos
 * dentro do prazo, mais a tabela de prazos (SLA) em vigor.
 *
 * Feito para ser mostrado ao contratante: numeros agregados, a porcentagem
 * no prazo logo no topo, CSV para quem quer conferir chamado a chamado, e
 * impressao limpa. Quem atende edita os prazos aqui mesmo.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  PERMISSIONS,
  TICKET_CATEGORY_LABELS,
  TICKET_PRIORITY_LABELS,
  type TicketCategory,
  type TicketPriority,
} from '@hotspot/shared';
import { ApiRequestError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  nomeDaLocalidade,
  type SlaConfigDto,
  type TicketLocationDto,
  type TicketMetricasDto,
  type TicketReportDto,
} from '../lib/types';
import { GraficoDeSerie } from '../components/Charts';
import { Alert, Button, Card, PageHeader, Spinner, StatCard } from '../components/ui';
import { duracao } from '../components/TicketBits';

const PRESETS = [
  { chave: '7', rotulo: '7 dias', dias: 7 },
  { chave: '30', rotulo: '30 dias', dias: 30 },
  { chave: '90', rotulo: '90 dias', dias: 90 },
] as const;

/** As linhas da tabela de prazos: as prioridades e o tipo com prazo proprio. */
const PRAZOS = ['urgente', 'alta', 'normal', 'baixa', 'trocaDeEquipamento'] as const;
const ROTULO_DO_PRAZO: Record<(typeof PRAZOS)[number], string> = {
  ...TICKET_PRIORITY_LABELS,
  trocaDeEquipamento: 'Troca de equipamento',
};

function hoje(): string {
  const [d, m, a] = new Date()
    .toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
    .split('/') as [string, string, string];
  return `${a}-${m}-${d}`;
}

function somarDias(iso: string, dias: number): string {
  const data = new Date(`${iso}T00:00:00Z`);
  data.setUTCDate(data.getUTCDate() + dias);
  return data.toISOString().slice(0, 10);
}

/** "92%" e o "12 de 13" que sustenta o numero; "—" sem nenhum caso. */
function noPrazo(dentro: number, fora: number): { pct: string; detalhe: string } {
  const total = dentro + fora;
  if (total === 0) return { pct: '—', detalhe: 'nenhum prazo encerrado' };
  return { pct: `${Math.round((dentro / total) * 100)}%`, detalhe: `${dentro} de ${total}` };
}

const tempo = (segundos: number | null) => (segundos === null ? '—' : duracao(segundos));

export function TicketReportPage() {
  const { can } = useAuth();
  const atende = can(PERMISSIONS.TICKET_MANAGE);

  const [preset, setPreset] = useState<string>('30');
  const [de, setDe] = useState(() => somarDias(hoje(), -29));
  const [ate, setAte] = useState(() => hoje());
  const [mikrotikId, setMikrotikId] = useState<number | null>(null);
  const [locais, setLocais] = useState<TicketLocationDto[]>([]);
  const [relatorio, setRelatorio] = useState<TicketReportDto | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    apiRequest<TicketLocationDto[]>('/admin/tickets/locations')
      .then(setLocais)
      .catch(() => setLocais([]));
  }, []);

  const consulta = useMemo(() => {
    const partes = [`de=${de}`, `ate=${ate}`];
    if (mikrotikId !== null) partes.push(`mikrotikId=${mikrotikId}`);
    return `?${partes.join('&')}`;
  }, [de, ate, mikrotikId]);

  useEffect(() => {
    let ativo = true;
    setRelatorio(null);
    setErro(null);
    apiRequest<TicketReportDto>(`/admin/tickets/report${consulta}`)
      .then((dados) => ativo && setRelatorio(dados))
      .catch(
        (caught: unknown) =>
          ativo &&
          setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar'),
      );
    return () => {
      ativo = false;
    };
  }, [consulta]);

  function escolherPreset(chave: string, dias: number) {
    setPreset(chave);
    const fim = hoje();
    setAte(fim);
    setDe(somarDias(fim, -(dias - 1)));
  }

  return (
    <div className="space-y-6">
      <Link
        to="/chamados"
        className="text-sm font-medium text-brand-700 hover:underline print:hidden"
      >
        ← Chamados
      </Link>

      <PageHeader
        title="Relatório de atendimento"
        description={`Chamados abertos de ${de.split('-').reverse().join('/')} a ${ate
          .split('-')
          .reverse()
          .join('/')}. Prazos conforme a tabela no fim da página.`}
        action={
          <div className="flex gap-2 print:hidden">
            <a
              href={`/api/admin/tickets/report.csv${consulta}`}
              className="inline-flex items-center rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-xs ring-1 ring-slate-200 ring-inset hover:bg-slate-50"
            >
              Exportar CSV
            </a>
            <Button variant="ghost" onClick={() => window.print()}>
              Imprimir
            </Button>
          </div>
        }
      />

      <Card className="print:hidden">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm shadow-sm">
            {PRESETS.map((opcao) => (
              <button
                key={opcao.chave}
                type="button"
                onClick={() => escolherPreset(opcao.chave, opcao.dias)}
                className={`rounded-md px-3 py-1.5 font-medium transition-colors ${
                  preset === opcao.chave
                    ? 'bg-brand-600 text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                {opcao.rotulo}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            De
            <input
              type="date"
              value={de}
              max={ate}
              onChange={(evento) => {
                if (!evento.target.value) return;
                setPreset('');
                setDe(evento.target.value);
              }}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm shadow-sm"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            até
            <input
              type="date"
              value={ate}
              min={de}
              max={hoje()}
              onChange={(evento) => {
                if (!evento.target.value) return;
                setPreset('');
                setAte(evento.target.value);
              }}
              className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm shadow-sm"
            />
          </label>
          {locais.length > 1 ? (
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Equipamento
              <select
                value={mikrotikId ?? ''}
                onChange={(evento) =>
                  setMikrotikId(evento.target.value ? Number(evento.target.value) : null)
                }
                className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm"
              >
                <option value="">Todos</option>
                {locais.map((local) => (
                  <option key={local.mikrotikId} value={local.mikrotikId}>
                    {local.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </Card>

      {erro ? <Alert>{erro}</Alert> : null}

      {!relatorio ? (
        erro ? null : (
          <Spinner label="Montando o relatório..." />
        )
      ) : (
        <Relatorio relatorio={relatorio} />
      )}

      <TabelaDePrazos podeEditar={atende} />
    </div>
  );
}

function Relatorio({ relatorio }: { relatorio: TicketReportDto }) {
  const { resumo } = relatorio;
  const resposta = noPrazo(resumo.respostaNoPrazo, resumo.respostaForaDoPrazo);
  const solucao = noPrazo(resumo.solucaoNoPrazo, resumo.solucaoForaDoPrazo);

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          label="Chamados no período"
          value={String(resumo.total)}
          hint={`${resumo.finalizados} finalizados · ${resumo.cancelados} cancelados${
            resumo.reChamados ? ` · ${resumo.reChamados} re-chamados` : ''
          }`}
        />
        <StatCard
          label="Em aberto"
          value={String(resumo.emAberto)}
          hint={resumo.atrasados ? `${resumo.atrasados} com a solução atrasada` : 'nenhum atrasado'}
        />
        <StatCard label="Tempo médio de 1ª resposta" value={tempo(resumo.mediaRespostaSeg)} />
        <StatCard label="Tempo médio de solução" value={tempo(resumo.mediaSolucaoSeg)} />
        <StatCard label="1ª resposta no prazo" value={resposta.pct} hint={resposta.detalhe} />
        <StatCard label="Solução no prazo" value={solucao.pct} hint={solucao.detalhe} />
      </div>

      <Card title="Chamados abertos por dia">
        <GraficoDeSerie
          pontos={relatorio.porDia.map((dia) => ({ dia: dia.dia, valor: dia.abertos }))}
          rotulo="Chamados abertos por dia"
          formatar={(valor) => `${valor} chamado${valor === 1 ? '' : 's'}`}
        />
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Por prioridade">
          <TabelaDeMetricas
            linhas={relatorio.porPrioridade.map((linha) => ({
              chave: linha.prioridade,
              rotulo:
                TICKET_PRIORITY_LABELS[linha.prioridade as TicketPriority] ?? linha.prioridade,
              ...linha,
            }))}
            vazio="Nenhum chamado no período."
          />
        </Card>

        <Card title="Por tipo">
          {relatorio.porCategoria.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum chamado no período.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {relatorio.porCategoria.map((linha) => (
                <li key={linha.categoria} className="flex justify-between">
                  <span className="text-slate-700">
                    {TICKET_CATEGORY_LABELS[linha.categoria as TicketCategory] ?? linha.categoria}
                  </span>
                  <span className="font-semibold text-slate-900 tabular-nums">{linha.total}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card title="Por praça">
        <TabelaDeMetricas
          linhas={relatorio.porLocal.map((linha) => ({
            chave: `${linha.mikrotikId ?? ''}|${linha.vlan ?? ''}`,
            rotulo:
              linha.mikrotikId === null
                ? 'Sem praça (dúvidas e pedidos)'
                : linha.vlan
                  ? `${nomeDaLocalidade(linha.vlan)} · ${linha.mikrotikName ?? 'equipamento removido'}`
                  : `${linha.mikrotikName ?? 'equipamento removido'} (todas as praças)`,
            ...linha,
          }))}
          vazio="Nenhum chamado no período."
        />
      </Card>
    </>
  );
}

function TabelaDeMetricas({
  linhas,
  vazio,
}: {
  linhas: (TicketMetricasDto & { chave: string; rotulo: string })[];
  vazio: string;
}) {
  if (linhas.length === 0) return <p className="text-sm text-slate-500">{vazio}</p>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs font-semibold tracking-wide text-slate-500 uppercase">
            <th className="py-2 pr-3" />
            <th className="py-2 pr-3 text-right">Chamados</th>
            <th className="py-2 pr-3 text-right">Em aberto</th>
            <th className="py-2 pr-3 text-right">1ª resposta</th>
            <th className="py-2 pr-3 text-right">Solução</th>
            <th className="py-2 pr-3 text-right">No prazo</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha) => {
            const prazo = noPrazo(linha.solucaoNoPrazo, linha.solucaoForaDoPrazo);
            return (
              <tr key={linha.chave} className="border-t border-slate-100">
                <td className="py-2 pr-3 text-slate-800">{linha.rotulo}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{linha.total}</td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {linha.emAberto}
                  {linha.atrasados ? (
                    <span className="ml-1 text-xs text-red-600">({linha.atrasados} atras.)</span>
                  ) : null}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {tempo(linha.mediaRespostaSeg)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {tempo(linha.mediaSolucaoSeg)}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums" title={prazo.detalhe}>
                  {prazo.pct}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-slate-500">
        Tempos médios de SLA. "No prazo" é a solução: finalizados dentro do prazo sobre os que já
        venceram ou foram finalizados.
      </p>
    </div>
  );
}

/** Horas com uma casa, do jeito que o contrato costuma escrever ("4", "0,5"). */
function horasDe(minutos: number): string {
  return String(Math.round((minutos / 60) * 10) / 10).replace('.', ',');
}

function minutosDe(horas: string): number {
  return Math.round(Number(horas.replace(',', '.')) * 60);
}

function TabelaDePrazos({ podeEditar }: { podeEditar: boolean }) {
  const [sla, setSla] = useState<SlaConfigDto | null>(null);
  const [rascunho, setRascunho] = useState<Record<string, string>>({});
  const [pausar, setPausar] = useState(true);
  const [janela, setJanela] = useState('24');
  const [editando, setEditando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState<{ tom: 'error' | 'success'; texto: string } | null>(
    null,
  );

  useEffect(() => {
    apiRequest<SlaConfigDto>('/admin/tickets/sla')
      .then(setSla)
      .catch(() => setSla(null));
  }, []);

  function comecar() {
    if (!sla) return;
    setRascunho(
      Object.fromEntries(
        PRAZOS.flatMap((p) => [
          [`${p}.resposta`, horasDe(sla[p].respostaMin)],
          [`${p}.solucao`, horasDe(sla[p].solucaoMin)],
        ]),
      ),
    );
    setPausar(sla.pausarEmPendente);
    setJanela(String(sla.reChamadoHoras));
    setMensagem(null);
    setEditando(true);
  }

  async function salvar(evento: FormEvent) {
    evento.preventDefault();
    setSalvando(true);
    setMensagem(null);
    try {
      const corpo = {
        ...Object.fromEntries(
          PRAZOS.map((p) => [
            p,
            {
              respostaMin: minutosDe(rascunho[`${p}.resposta`] ?? ''),
              solucaoMin: minutosDe(rascunho[`${p}.solucao`] ?? ''),
            },
          ]),
        ),
        pausarEmPendente: pausar,
        reChamadoHoras: Math.round(Number(janela.replace(',', '.'))),
      };
      setSla(await apiRequest<SlaConfigDto>('/admin/tickets/sla', { method: 'PUT', body: corpo }));
      setEditando(false);
      setMensagem({ tom: 'success', texto: 'Prazos salvos. Valem para os chamados novos.' });
    } catch (caught) {
      setMensagem({
        tom: 'error',
        texto: caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao salvar',
      });
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Card
      title="Prazos de atendimento (SLA)"
      action={
        podeEditar && sla && !editando ? (
          <Button variant="ghost" size="sm" onClick={comecar} className="print:hidden">
            Editar prazos
          </Button>
        ) : null
      }
    >
      {!sla ? (
        <Spinner label="Carregando os prazos..." />
      ) : (
        <form onSubmit={salvar} className="space-y-3">
          {mensagem ? <Alert tone={mensagem.tom}>{mensagem.texto}</Alert> : null}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold tracking-wide text-slate-500 uppercase">
                <th className="py-2 pr-3">Prioridade / tipo</th>
                <th className="py-2 pr-3">1ª resposta em até</th>
                <th className="py-2 pr-3">Solução em até</th>
              </tr>
            </thead>
            <tbody>
              {PRAZOS.map((p) => (
                <tr key={p} className="border-t border-slate-100">
                  <td className="py-2 pr-3 font-medium text-slate-800">{ROTULO_DO_PRAZO[p]}</td>
                  {(['resposta', 'solucao'] as const).map((campo) => (
                    <td key={campo} className="py-2 pr-3 text-slate-700">
                      {editando ? (
                        <label className="flex items-center gap-2">
                          <input
                            value={rascunho[`${p}.${campo}`] ?? ''}
                            onChange={(evento) =>
                              setRascunho((atual) => ({
                                ...atual,
                                [`${p}.${campo}`]: evento.target.value,
                              }))
                            }
                            inputMode="decimal"
                            required
                            aria-label={`${ROTULO_DO_PRAZO[p]}: ${campo === 'resposta' ? 'primeira resposta' : 'solução'} em horas`}
                            className="w-20 rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm"
                          />
                          horas
                        </label>
                      ) : (
                        duracao(
                          (campo === 'resposta' ? sla[p].respostaMin : sla[p].solucaoMin) * 60,
                        )
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {editando ? (
            <div className="space-y-3 rounded-xl bg-slate-50 p-4 text-sm text-slate-700">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={pausar}
                  onChange={(evento) => setPausar(evento.target.checked)}
                  className="mt-0.5 size-4 rounded border-slate-300 text-brand-600"
                />
                <span>
                  Pausar o prazo enquanto o chamado está Pendente (aguardando quem abriu).
                  <span className="block text-xs text-slate-500">
                    Desmarque se o contrato conta horas corridas desde a abertura.
                  </span>
                </span>
              </label>
              <label className="flex flex-wrap items-center gap-2">
                Re-chamado: falha no mesmo ponto até
                <input
                  value={janela}
                  onChange={(evento) => setJanela(evento.target.value)}
                  inputMode="numeric"
                  required
                  aria-label="Janela do re-chamado em horas"
                  className="w-16 rounded-lg border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm"
                />
                horas depois de resolvido usa só o tempo que sobrou do prazo (0 desliga).
              </label>
            </div>
          ) : null}
          <p className="text-xs text-slate-500">
            Tempo corrido, 24 horas por dia.{' '}
            {sla.pausarEmPendente
              ? 'O relógio para enquanto o chamado está Pendente (aguardando quem abriu).'
              : 'O relógio não para, nem com o chamado Pendente.'}{' '}
            {sla.reChamadoHoras > 0
              ? `Re-chamado: uma nova falha no mesmo ponto até ${sla.reChamadoHoras}h depois de resolvida usa só o tempo que sobrou do prazo — a soma dos tempos precisa caber nele.`
              : 'Re-chamado desligado.'}{' '}
            Troca de equipamento tem prazo próprio. Mudar os prazos vale para os chamados abertos
            depois.
          </p>
          {editando ? (
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setEditando(false)}
                disabled={salvando}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar prazos'}
              </Button>
            </div>
          ) : null}
        </form>
      )}
    </Card>
  );
}
